import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
  FileText, 
  Search, 
  FileCheck, 
  Banknote,
  ArrowRight,
  Phone,
  CheckCircle,
  Clock,
  Users,
  Shield
} from 'lucide-react';

export default function HowItWorksPage() {
  const { t } = useLanguage();

  const processSteps = [
    {
      step: 1,
      icon: FileText,
      title: t('process.step1Title'),
      subtitle: t('howItWorks.step1Subtitle'),
      description: t('howItWorks.step1Desc'),
      details: [
        t('quote.propertyAddress'),
        t('quote.purchasePrice'),
        t('quote.loanAmount'),
        t('quote.additionalDetails'),
      ],
      timeline: t('howItWorks.step1Timeline'),
    },
    {
      step: 2,
      icon: Search,
      title: t('process.step2Title'),
      subtitle: t('howItWorks.step2Subtitle'),
      description: t('howItWorks.step2Desc'),
      details: [
        t('valueProps.assetBased'),
        t('trustStrip.fastDecisions'),
        t('loans.loanTerms'),
        t('contact.expect3'),
      ],
      timeline: t('howItWorks.step2Timeline'),
    },
    {
      step: 3,
      icon: FileCheck,
      title: t('process.step3Title'),
      subtitle: t('howItWorks.step3Subtitle'),
      description: t('howItWorks.step3Desc'),
      details: [
        t('valueProps.assetBased'),
        t('loans.loanTerms'),
        t('contact.expect4'),
        t('footer.disclaimer').substring(0, 40) + '...',
      ],
      timeline: t('howItWorks.step3Timeline'),
    },
    {
      step: 4,
      icon: Banknote,
      title: t('process.step4Title'),
      subtitle: t('howItWorks.step4Subtitle'),
      description: t('howItWorks.step4Desc'),
      details: [
        t('trustStrip.fastDecisions'),
        t('trustStrip.directCapital'),
        t('loans.fastClosing'),
        t('contact.expect4'),
      ],
      timeline: t('howItWorks.step4Timeline'),
    },
  ];

  const advantages = [
    {
      icon: Clock,
      title: t('howItWorks.speedTitle'),
      description: t('howItWorks.speedDesc'),
    },
    {
      icon: Users,
      title: t('howItWorks.directTitle'),
      description: t('howItWorks.directDesc'),
    },
    {
      icon: Shield,
      title: t('howItWorks.certaintyTitle'),
      description: t('howItWorks.certaintyDesc'),
    },
  ];

  return (
    <div className="pt-20" data-testid="how-it-works-page">
      {/* Hero Section */}
      <section className="bg-slate-900 py-20 md:py-32">
        <div className="container-main">
          <div className="max-w-3xl">
            <p className="font-body text-amber-500 uppercase tracking-widest text-sm font-semibold mb-4">
              {t('howItWorks.pageSubtitle')}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('howItWorks.pageTitle')}
            </h1>
            <p className="font-body text-slate-300 text-lg">
              {t('howItWorks.pageDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="space-y-0">
            {processSteps.map((item, index) => (
              <div 
                key={item.step}
                className="relative"
                data-testid={`process-step-${item.step}`}
              >
                {/* Connector Line */}
                {index < processSteps.length - 1 && (
                  <div className="absolute left-7 top-28 bottom-0 w-0.5 bg-slate-200 hidden md:block" />
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 py-12">
                  {/* Step Number & Icon */}
                  <div className="md:col-span-1">
                    <div className="w-14 h-14 bg-slate-900 flex items-center justify-center relative z-10">
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="md:col-span-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-body text-amber-700 font-semibold text-sm uppercase tracking-wider">
                        Step {item.step}
                      </span>
                      <span className="text-slate-300">|</span>
                      <span className="font-body text-slate-500 text-sm">
                        {item.timeline}
                      </span>
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                      {item.title}
                    </h2>
                    <p className="font-body text-amber-700 font-medium mb-4">
                      {item.subtitle}
                    </p>
                    <p className="font-body text-slate-600 text-lg">
                      {item.description}
                    </p>
                  </div>
                  
                  {/* Details */}
                  <div className="md:col-span-5">
                    <div className="bg-slate-50 border border-slate-200 p-6">
                      <h3 className="font-body font-semibold text-slate-900 mb-4">
                        {t('howItWorks.whatsInvolved')}
                      </h3>
                      <ul className="space-y-3">
                        {item.details.map((detail, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                            <span className="font-body text-slate-700 text-sm">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                
                {index < processSteps.length - 1 && (
                  <div className="border-b border-slate-200" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <p className="font-body text-amber-700 uppercase tracking-widest text-sm font-semibold mb-4">
              {t('howItWorks.advantageSubtitle')}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('howItWorks.advantageTitle')}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {advantages.map((adv, index) => (
              <div 
                key={index}
                className="card-institutional text-center"
                data-testid={`advantage-${index}`}
              >
                <div className="w-16 h-16 bg-slate-900 flex items-center justify-center mx-auto mb-6">
                  <adv.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                  {adv.title}
                </h3>
                <p className="font-body text-slate-600">
                  {adv.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-20">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            {t('howItWorks.readyTitle')}
          </h2>
          <p className="font-body text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            {t('howItWorks.readyDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="btn-gold rounded-none" data-testid="hiw-cta-btn">
                {t('loans.submitYourDeal')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:424-303-2130">
              <Button className="btn-secondary rounded-none bg-transparent border-white text-white hover:bg-white/10">
                <Phone className="mr-2 w-5 h-5" />
                {t('cta.call')} 424-303-2130
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
